import React, { useState } from 'react';
import {
  Button,
  Tag,
  Space,
  Modal,
  Select,
  Row,
  Col,
  Card,
  Statistic,
  message,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useNotificationTemplates } from '../../hooks/useNotificationTemplates';
import { DataTable } from '../../components';
import { NotifyTemplateType, CommonState } from '../../services/notification-template.service';
import type { NotifyTemplate } from '../../services/notification-template.service';

const typeLabels: Record<string, string> = {
  [NotifyTemplateType.NONE]: 'None',
  [NotifyTemplateType.ARTICLE]: 'Article',
  [NotifyTemplateType.PRODUCT]: 'Product',
  [NotifyTemplateType.ORDER]: 'Order',
  [NotifyTemplateType.OPEN_LINK]: 'Open Link',
};

const typeColors: Record<string, string> = {
  [NotifyTemplateType.NONE]: 'default',
  [NotifyTemplateType.ARTICLE]: 'blue',
  [NotifyTemplateType.PRODUCT]: 'purple',
  [NotifyTemplateType.ORDER]: 'green',
  [NotifyTemplateType.OPEN_LINK]: 'orange',
};

const statusColors: Record<number, string> = {
  [CommonState.ENABLED]: 'success',
  [CommonState.DISABLED]: 'error',
};

export const NotificationTemplateList: React.FC = () => {
  const navigate = useNavigate();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [filters, setFilters] = useState({
    type: undefined as string | undefined,
    state: undefined as number | undefined,
    search: '',
  });

  const {
    templates,
    loading,
    meta,
    stats,
    setFilters: setTableFilters,
    refetch,
    deleteTemplate,
    deleteManyTemplates,
    cloneTemplate,
  } = useNotificationTemplates();

  const handleSearch = (value: string) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setTableFilters({ ...filters, search: value, page: 1 });
  };

  const handleFilterChange = (key: string, value: string | number | undefined) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setTableFilters({ ...newFilters, page: 1 });
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setTableFilters({ ...filters, page, limit: pageSize });
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTemplate(id);
      message.success('Template deleted successfully');
    } catch {
      message.error('Failed to delete template');
    }
  };

  const handleClone = async (id: string) => {
    try {
      await cloneTemplate(id);
      message.success('Template cloned successfully');
    } catch {
      message.error('Failed to clone template');
    }
  };

  const handleDeleteMany = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select templates to delete');
      return;
    }
    Modal.confirm({
      title: 'Delete Templates',
      content: `Are you sure you want to delete ${selectedRowKeys.length} template(s)?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteManyTemplates(selectedRowKeys as string[]);
          setSelectedRowKeys([]);
          message.success('Templates deleted successfully');
        } catch {
          message.error('Failed to delete templates');
        }
      },
    });
  };

  const columns: ColumnsType<NotifyTemplate> = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record) => (
        <Space direction="vertical" size={0}>
          <span>{text}</span>
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>{record.code}</span>
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type: string) => (
        <Tag color={typeColors[type] || 'default'}>{typeLabels[type] || type}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'state',
      key: 'state',
      width: 100,
      render: (state: number) => (
        <Tag color={statusColors[state] || 'default'}>
          {state === CommonState.ENABLED ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm'),
    },
  ];

  const filterContent = (
    <Row gutter={16}>
      <Col span={8}>
        <Select
          placeholder="Filter by type"
          allowClear
          style={{ width: '100%' }}
          value={filters.type}
          onChange={(value) => handleFilterChange('type', value)}
          options={[
            { value: NotifyTemplateType.NONE, label: 'None' },
            { value: NotifyTemplateType.ARTICLE, label: 'Article' },
            { value: NotifyTemplateType.PRODUCT, label: 'Product' },
            { value: NotifyTemplateType.ORDER, label: 'Order' },
            { value: NotifyTemplateType.OPEN_LINK, label: 'Open Link' },
          ]}
        />
      </Col>
      <Col span={8}>
        <Select
          placeholder="Filter by status"
          allowClear
          style={{ width: '100%' }}
          value={filters.state}
          onChange={(value) => handleFilterChange('state', value)}
          options={[
            { value: CommonState.ENABLED, label: 'Active' },
            { value: CommonState.DISABLED, label: 'Inactive' },
          ]}
        />
      </Col>
    </Row>
  );

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic title="Total Templates" value={meta?.total || 0} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Active" value={stats.active} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Inactive" value={stats.inactive} valueStyle={{ color: '#f5222d' }} />
          </Card>
        </Col>
      </Row>

      <DataTable
        title="Notification Templates"
        data={templates}
        columns={columns}
        loading={loading}
        rowKey="_id"
        searchable
        searchPlaceholder="Search by title or code..."
        onSearch={handleSearch}
        onRefresh={refetch}
        filters={filterContent}
        pagination={{
          current: meta?.page || 1,
          pageSize: meta?.limit || 10,
          total: meta?.total || 0,
          onChange: handlePageChange,
        }}
        selectionType="checkbox"
        selectedRowKeys={selectedRowKeys}
        onSelectionChange={setSelectedRowKeys}
        extraActions={
          <Space>
            {selectedRowKeys.length > 0 && (
              <Button danger icon={<DeleteOutlined />} onClick={handleDeleteMany}>
                Delete Selected ({selectedRowKeys.length})
              </Button>
            )}
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/notification-templates/add')}>
              Add Template
            </Button>
          </Space>
        }
        actionColumn={{
          width: 150,
          fixed: 'right',
          actions: (record) => [
            {
              key: 'edit',
              label: 'Edit',
              icon: <EditOutlined />,
              onClick: () => navigate(`/notification-templates/${record._id}/edit`),
            },
            {
              key: 'clone',
              label: 'Clone',
              icon: <CopyOutlined />,
              onClick: () => handleClone(record._id as string),
            },
            {
              key: 'delete',
              label: 'Delete',
              icon: <DeleteOutlined />,
              danger: true,
              onClick: () => {
                Modal.confirm({
                  title: 'Delete Template',
                  content: `Are you sure you want to delete "${record.title}"?`,
                  onOk: () => handleDelete(record._id as string),
                });
              },
            },
          ],
        }}
      />
    </div>
  );
};

export default NotificationTemplateList;
